import React from 'react'
import { Box, Typography } from '@mui/material'
import { Home as HomeIcon } from '@mui/icons-material'
import BackButton from './BackButton'
import { crmColors } from '../../theme/colors'

/**
 * EduOKWeb-style content-header: breadcrumbs/title (left), Back + actions (right).
 */
const PageContentHeader = ({
  title,
  subtitle,
  breadcrumbs,
  onBack,
  showBack = true,
  titleIcon,
  actions,
}) => {
  if (!title && !showBack && !breadcrumbs?.length) {
    return null
  }

  const crumbItems =
    breadcrumbs?.length > 0
      ? breadcrumbs
      : title
        ? [{ label: title, icon: titleIcon }]
        : []

  return (
    <Box
      component="header"
      className="page-content-header content-header"
      sx={{
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 2,
        flexWrap: 'wrap',
        minHeight: 40,
        px: 0,
        py: 0,
        backgroundColor: crmColors.primary.dark,
        backgroundImage: `linear-gradient(135deg, ${crmColors.primary.dark} 0%, ${crmColors.primary.darker} 100%)`,
        color: '#ffffff',
        borderBottom: `1px solid ${crmColors.primary.darker}`,
        boxShadow: '0 1px 4px rgba(15, 23, 42, 0.12)',
      }}
    >
      <Box className="leftside-content-header" sx={{ flex: 1, minWidth: 0, py: 0.5 }}>
        {crumbItems.length > 0 && (
          <Box
            component="ul"
            className="breadcrumbs"
            sx={{
              listStyle: 'none',
              m: 0,
              p: 0,
              pl: { xs: 2, md: 2.5 },
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 0.5,
              lineHeight: 1.4,
            }}
          >
            {crumbItems.map((crumb, index) => (
              <Box
                component="li"
                key={`${crumb.label}-${index}`}
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  fontSize: { xs: '0.9rem', md: '1rem' },
                  fontWeight: 600,
                  verticalAlign: 'middle',
                  '&:not(:first-of-type)::before': {
                    fontFamily: 'Font Awesome 5 Free',
                    content: '"\\f105"',
                    fontWeight: 700,
                    px: 0.75,
                    color: crmColors.primary.accent,
                    fontSize: '0.85rem',
                  },
                }}
              >
                {index === 0 && (
                  <Box
                    component="span"
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      pr: 1,
                      color: '#ffffff',
                    }}
                  >
                    {crumb.icon ?? titleIcon ?? (
                      <HomeIcon sx={{ fontSize: 18, verticalAlign: 'middle', color: '#ffffff' }} />
                    )}
                  </Box>
                )}
                {index > 0 && crumb.icon && (
                  <Box component="span" sx={{ display: 'inline-flex', pr: 0.75 }}>
                    {crumb.icon}
                  </Box>
                )}
                <Box
                  component="span"
                  className="breadcrumb-label"
                  sx={{
                    color: '#ffffff',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    maxWidth: { xs: '70vw', md: 'none' },
                  }}
                >
                  {crumb.label}
                </Box>
              </Box>
            ))}
          </Box>
        )}
        {subtitle && (
          <Typography
            variant="body2"
            className="page-subtitle"
            sx={{
              color: 'rgba(255, 255, 255, 0.8)',
              pl: { xs: 2, md: 2.5 },
              pr: 2,
              pb: 0.75,
              mt: crumbItems.length ? -0.25 : 0.75,
              fontSize: '0.8rem',
            }}
          >
            {subtitle}
          </Typography>
        )}
      </Box>

      <Box
        className="rightside-content-header"
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          flexWrap: 'wrap',
          flexShrink: 0,
          pr: { xs: 2, md: 2.5 },
          pl: 1,
        }}
      >
        {actions}
        {showBack && onBack && <BackButton onClick={onBack} />}
      </Box>
    </Box>
  )
}

export default PageContentHeader
